<template>
    <div>
        <b-card no-body class="account mb-4" v-if="account.id">
            <b-card-body>
                <b-row v-if="account && account.id">
                    <b-col sm="12" md="6">
                        <h4 class="mb-3 title">{{ account.nickname }} <i class="fad fa-pencil ml-2 fa-xs" v-b-modal.nickname></i></h4>

                        <h6 class="mb-0">24/7 Educational Services</h6>
                        <div class="meta mb-3"><strong>Account</strong> R{{ account.id }}</div>

                        <div class="mt-1 text-success" v-if="account.paid < account.total && account.active === 1 && account.is_error === 0 && account.payment_method && account.payment_method.fatt_method_id"><small><i class="fas fa-play-circle mr-2"></i>Automatic Payments Active</small></div>
                        <div class="mt-1 text-muted" v-if="account.paid >= account.total && account.active === 1 && account.is_error === 0 && account.payment_method && account.payment_method.fatt_method_id"><small><i class="fas fa-stop-circle mr-2"></i>Automatic Payments Inactive</small></div>

                    </b-col>
                    <b-col class="account-total text-right" md="6" sm="12">
                        <div>
                            <b-button variant="success" size="md" class="mt-3" v-b-modal.pay :disabled="account.paid >= account.total || account.is_error === 1 || account.is_default === 1 || account.is_collections === 1 || !account.payment_method || !account.payment_method.fatt_method_id"><i class="fad fa-money-check-edit fa-flip-horizontal mr-2"></i>Make Additional Payment</b-button>
                        </div>
                        <div class="meta mt-3">Current Balance</div>
                        <div>{{ account.total - account.paid | currency }}</div>
                    </b-col>
                </b-row>
                <b-row class="mt-4" v-if="account.is_error === 1">
                    <b-col>
                        <b-alert variant="danger" show>
                            <div class="mt-2" style="font-size: 1rem;"><strong><i class="fas fa-exclamation-circle mr-2"></i>There was a problem processing your last payment.</strong></div>
                            <div class="mt-2 ml-4">
                                <div><strong>Message: </strong>{{ account.last_error_msg }}</div>
                                <div class="mt-2">Please resolve this issue in a timely manner to avoid additional fees or risk your account going into default. Most issues are resolved by correcting problems with your payment method or contacting your bank. If you need assistance please <a href="mailto:help@thepaymentoption.com">reach out to us</a>.</div>
                                <div class="mt-2">Please note that automatic payments will be retried automatically when this issue is resolved. Additional payments will not.</div>
                                <div class="mt-4 mb-3">
                                    <b-button variant="danger" @click="doResolveError"><i class="fas fa-check mr-2"></i>Mark Resolved</b-button>
                                </div>
                            </div>
                        </b-alert>
                    </b-col>
                </b-row>
            </b-card-body>

            <div class="alerts-container">
                <!-- Collections Alert (HIGHEST PRIORITY) -->
                <b-alert show variant="danger" class="mb-3" v-if="showCollectionsNotification" dismissible @dismissed="collectionsNotificationHidden = true">
                    <h6 class="mb-2"><i class="fas fa-exclamation-triangle mr-2"></i>Account in Collections</h6>
                    <p class="mb-2">
                        Your account has been sent to collections. Please work with your assigned collection attorney to resolve this matter.
                    </p>
                    <p class="mb-0">
                        If you need assistance or have questions, please contact us at <a href="mailto:help@thepaymentoption.com">help@thepaymentoption.com</a>.  However, please note that we are not able to provide any assistance with collections matters and will simply refer you to your assigned collection attorney.
                    </p>
                </b-alert>

                <!-- Default Alert (HIGH PRIORITY) -->
                <b-alert show variant="danger" class="mb-3" v-if="showDefaultNotification" dismissible @dismissed="defaultNotificationHidden = true">
                    <h6 class="mb-2"><i class="fas fa-exclamation-circle mr-2"></i>Account in Default</h6>
                    <p class="mb-2">
                        Your account is currently in default status. Immediate action is required.
                    </p>
                    <p class="mb-0">
                        Please contact us at <a href="mailto:help@thepaymentoption.com">help@thepaymentoption.com</a> to resolve your account status.
                    </p>
                </b-alert>

                <!-- Past Due Warning -->
                <b-alert show variant="warning" class="mb-3" v-if="showPastDueNotification" dismissible @dismissed="pastDueNotificationHidden = true">
                    <h6 class="mb-2"><i class="fas fa-exclamation-triangle mr-2"></i>Account Past Due</h6>
                    <p class="mb-0">
                        You are {{ account.past_due_amount | currency }} behind on your payment schedule.
                        Please <a href="mailto:help@thepaymentoption.com">contact us</a> if you require assistance in this matter.
                    </p>
                </b-alert>

                <!-- Queued Payment Info -->
                <b-alert show variant="info" class="mb-3" v-if="showQueuedPaymentNotification" dismissible @dismissed="paymentNotificationHidden = true">
                    <h6 class="mb-2"><i class="fas fa-info-circle mr-2"></i>Payment Processing</h6>
                    <p class="mb-2">
                        Your automatic payment of <strong>{{ account.queued_payment_amount | currency }}</strong> is currently being processed. If you do not see your payment yet, please be patient as it is in the queue and will be processed shortly. If this is unexpected, please <a href="mailto:help@thepaymentoption.com">contact us</a>.
                    </p>
                    <p class="mb-0">
                        <strong>Please do not submit an additional payment to cover this installment.</strong> It is taken automatically. Additional payments are extra and do not replace it.
                    </p>
                </b-alert>

                <!-- Upcoming Payment Info -->
                <b-alert show variant="info" class="mb-3" v-if="showUpcomingPaymentNotification" dismissible @dismissed="upcomingNotificationHidden = true">
                    <h6 class="mb-2"><i class="fas fa-calendar-check mr-2"></i>Upcoming Automatic Payment</h6>
                    <p class="mb-2">
                        Your automatic payment of <strong>{{ account.queued_payment_amount | currency }}</strong> is scheduled for <strong>{{ account.next_charge_on | moment('dddd, MMMM Do') }}</strong><span v-if="account.payment_method && account.payment_method.last_four"> from {{ account.payment_method.nickname }} ({{ account.payment_method.last_four }})</span>.
                    </p>
                    <p class="mb-0">
                        <strong>Please do not submit an additional payment to cover this installment.</strong> It is taken automatically. Additional payments are extra and do not replace it.
                    </p>
                </b-alert>

                <!-- Washington State Sales Tax Notice -->
                <b-alert show variant="warning" class="mb-3" v-if="false && account.id && !taxNoticeHidden" dismissible @dismissed="taxNoticeHidden = true">
                    <h6 class="mb-2"><i class="fas fa-info-circle mr-2"></i>Important Tax Notice</h6>
                    <p>
                        <strong>Effective October 1, 2025:</strong> Washington State sales tax will apply retroactively to any remaining balance on existing agreements.
                        More details will be made available on October 1, 2025 on how this sales tax will be collected.
                    </p>
                    <p>
                        In the meantime, you have the option to pay down or pay off your balance.  Sales tax will be calculated based on your remaining balance so paying it down reduces or eliminates the amount of sales tax you will owe.
                        To do so, you can use the <strong>'Make Additional Payment'</strong> button above to pay what you choose on your account.
                    </p>
                    <p>
                        <strong>Limited Time Offer:</strong> Until October 1, 2025, we are offering the ability to pay down your balance by credit card with a limited additional fee of only 3%.
                        If you wish to take advantage of this offer, please click the button below.
                    </p>
                    <p>
                        Please note that this is NOT to pay sales tax at this time.  Please also note that this does not affect automatic payments which will be charged as scheduled unless payed in full.
                    </p>
                    <div class="mt-4 mb-3">
                        <b-button variant="primary" v-b-modal.payCreditCard :disabled="account.paid >= account.total || account.is_default === 1 || account.is_collections === 1">
                            <i class="fas fa-credit-card mr-2"></i>Offer: Make Additional Payment by Credit Card
                        </b-button>
                    </div>
                </b-alert>

                <!-- Unpaid Sales Tax Alert -->
                <b-alert show variant="danger" class="mb-0" v-if="account.id && unpaidTaxAmount > 0 && !unpaidTaxAlertHidden" dismissible @dismissed="unpaidTaxAlertHidden = true">
                    <h6 class="mb-2"><i class="fas fa-exclamation-circle mr-2"></i>Washington State Sales Tax Due</h6>
                    <p>
                        <strong>You have an unpaid sales tax balance of {{ unpaidTaxAmount | currency }}</strong><br>
                    </p>
                    <p>
                        Sales tax has been calculated based on your balance as of October 1, 2025.
                    </p>
                    <p>
                        <strong>Past Due - Due Now</strong>
                    </p>
                    <div class="mt-3 mb-2">
                        <b-button variant="danger" @click="openTaxPaymentModal" :disabled="account.is_collections === 1">
                            <i class="fas fa-file-invoice-dollar mr-2"></i>View Details & Pay Sales Tax
                        </b-button>
                    </div>
                </b-alert>
            </div>

            <b-tabs card>
                <b-tab no-body>
                    <template v-slot:title><i class="fad fa-usd-circle mr-2"></i>Activity</template>

                    <div class="d-xs-none d-sm-none d-md-block">
                        <table class="transaction-table table b-table">
                            <thead class="">
                            <tr>
                                <th role="columnheader" style="width: 10px; padding-right: 0;"></th>
                                <th role="columnheader">DATE</th>
                                <th role="columnheader">DESCRIPTION</th>
                                <th role="columnheader">STATUS</th>
                                <th role="columnheader">AMOUNT</th>
                            </tr>
                            </thead>

                            <tbody>
                            <template v-for="t in account.transactions">
                                <tr style="cursor: pointer" @click="t.expanded = !t.expanded">
                                    <td style="width: 28px; padding-right: 0;"><i class="fas" :class="{'fa-angle-right': !t.expanded, 'fa-angle-down': t.expanded}" v-if="t.trans_id"></i></td>
                                    <td style="white-space: nowrap; width: 40px;">{{ t.created_at | moment('MMM Do, YYYY') }}</td>
                                    <td>{{ t.title }}</td>
                                    <td style="white-space: nowrap;">
                                        <span v-if="t.result === 'failure'" v-b-popover.hover.top="t.fail_msg"><i class="fad mr-2 fa-fw fa-ban text-danger"></i>Failed<i v-if="t.fail_msg" class="fal fa-xs fa-question-circle ml-1"></i></span>
                                        <template v-else>
                                            <template v-if="t.result === 'requested'">
                                                <span style="cursor: default" v-b-popover.hover.top="'This additional payment requires e-mail approval before it will be processed. Check your e-mail.'"><i class="fad mr-2 fa-fw fa-exclamation-triangle text-warning"></i>Awaiting E-Mail Approval<i class="fal fa-xs fa-question-circle ml-1"></i></span>
                                            </template>
                                            <template v-else>
                                                <template v-if="!t.is_fee">
                                                    <span v-if="t.is_complete === 1"><i class="fad mr-2 fa-fw fa-check-circle text-success"></i>Complete</span>
                                                    <span v-else style="cursor: default" v-b-popover.hover.top="'This payment has not yet cleared and is therefore pending. This is normal with ACH Debit transactions and may take up to 7-10 days.'"><i class="fad mr-2 fa-fw fa-usd-circle text-warning"></i>Submitted, Pending<i class="fal fa-xs fa-question-circle ml-1"></i></span>
                                                </template>
                                            </template>
                                        </template>
                                    </td>
                                    <td>
                                        <template v-if="t.is_fee">({{ t.total | currency }})</template>
                                        <template v-else-if="t.type === 'note'"> </template>
                                        <template v-else>{{ t.total | currency }}</template>
                                    </td>
                                </tr>
                                <tr v-if="t.expanded && t.trans_id">
                                    <td style="width: 28px; padding-right: 0;">&nbsp;</td>
                                    <td>&nbsp;</td>
                                    <td colspan="3" style="font-size: 0.7rem; color: #555;">
                                        <div class="mb-2" style="font-size: 0.8rem; color: #777;" v-if="t.is_complete === 0"><i class="fal fa-xs fa-question-circle mr-2"></i>This payment has not yet cleared and is therefore pending.<br>This is normal with ACH Debit transactions and may take up to 7-10 days.</div>
                                        <div v-if="t.settled_at"><strong>Estimated to Clear:</strong> <span class="text-muted">{{ t.settled_at | moment('dddd, MMMM Do, YYYY') }}</span></div>
                                        <div v-else><strong>Estimated to Clear:</strong> <span class="text-muted">N/A</span></div>
                                        <div v-if="t.completed_at"><strong>Completed:</strong> <span class="text-muted">{{ t.completed_at | moment('dddd, MMMM Do, YYYY') }}</span></div>
                                        <div><strong>Transaction Id:</strong> <span class="text-muted">{{ t.trans_id }}</span></div>
                                        <div><strong>Authorization:</strong> <span class="text-muted">{{ t.auth_code }}</span></div>
                                        <div v-if="t.fail_msg"><strong>Failure Message:</strong> <span class="text-muted">{{ t.fail_msg }}</span></div>
                                    </td>
                                </tr>
                            </template>

                            </tbody>

                        </table>
                    </div>
                    <div class="transactions-mobile d-sm-block d-md-none">
                        <div v-for="t in account.transactions" class="payment-item">
                            <b-row style="cursor: pointer" @click="t.expanded = !t.expanded" class="pt-3 pb-3" no-gutters>
                                <b-col cols="0"><i class="fas fa-fw mr-1" :class="{'fa-angle-right': (t.trans_id && !t.expanded), 'fa-angle-down': (t.trans_id && t.expanded)}"></i></b-col>
                                <b-col>
                                    <div><strong>{{ t.title }}</strong></div>
                                    <div>{{ t.created_at | moment('MMM Do, YYYY') }}</div>
                                    <div>
                                        <span v-if="t.result === 'failure'"><i class="fad fa-fw mr-2 fa-ban text-danger"></i>Failed</span>
                                        <template v-else>
                                            <template v-if="t.result === 'requested'">
                                                <span style="cursor: default"><i class="fad fa-fw mr-2 fa-exclamation-triangle text-warning"></i>Awaiting E-Mail Approval</span>
                                            </template>
                                            <template v-else>
                                                <template v-if="!t.is_fee">
                                                    <span v-if="t.is_complete === 1"><i class="fad fa-fw mr-2 fa-check-circle text-success"></i>Complete</span>
                                                    <span v-else style="cursor: default"><i class="fad fa-fw mr-2 fa-usd-circle text-warning"></i>Submitted, Pending</span>
                                                </template>
                                            </template>
                                        </template>
                                    </div>
                                </b-col>

                                <b-col class="text-right pr-3">
                                    <template v-if="t.is_fee">({{ t.total | currency }})</template>
                                    <template v-else-if="t.type === 'note'"> </template>
                                    <template v-else>{{ t.total | currency }}</template>
                                </b-col>
                            </b-row>
                            <b-row v-if="t.expanded && t.trans_id" class="pb-3">
                                <b-col cols="0"><i class="fas fa-fw mr-1"></i></b-col>
                                <b-col>
                                    <div style="font-size: 0.7rem; color: #555;">
                                        <div class="mb-2" style="font-size: 0.8rem; color: #777;" v-if="t.is_complete === 0"><i class="fal fa-xs fa-question-circle mr-2"></i>This payment has not yet cleared and is therefore pending.<br>This is normal with ACH Debit transactions and may take up to 7-10 days.</div>
                                        <div v-if="t.settled_at"><strong>Estimated to Clear:</strong> <span class="text-muted">{{ t.settled_at | moment('dddd, MMMM Do, YYYY') }}</span></div>
                                        <div v-else><strong>Estimated to Clear:</strong> <span class="text-muted">N/A</span></div>
                                        <div v-if="t.completed_at"><strong>Completed:</strong> <span class="text-muted">{{ t.completed_at | moment('dddd, MMMM Do, YYYY') }}</span></div>
                                        <div><strong>Transaction Id:</strong> <span class="text-muted">{{ t.trans_id }}</span></div>
                                        <div><strong>Authorization:</strong> <span class="text-muted">{{ t.auth_code }}</span></div>
                                        <div v-if="t.fail_msg"><strong>Failure Message:</strong> <span class="text-muted">{{ t.fail_msg }}</span></div>
                                    </div>
                                </b-col>
                            </b-row>
                        </div>
                    </div>
                    <div class="mt-5 ml-3 text-muted" v-if="account.transactions.length === 0"><em>There are no transactions for this account yet.</em></div>
                </b-tab>
                <b-tab no-body>
                    <template v-slot:title><i class="fad fa-file-invoice mr-2"></i>Details</template>
                    <table class="details-table mt-3">
                        <tr>
                            <th class="no-border">Merchant</th>
                            <td class="no-border"><strong>24/7 Educational Services</strong></td>
                        </tr>
                        <tr>
                            <th class="no-border"></th>
                            <td class="no-border">
                                <div><a href="https://www.my247ed.com" target="_blank">https://www.my247ed.com</a> <i style="font-size: 0.7rem; color: #666" class="far fa-external-link ml-2"></i></div>
                                <div class="mt-1"><a href="mailto:information@my247ed.com">information@my247ed.com</a></div>
                                <div class="mt-1">(509) 448-0603</div>
                                <div class="mt-1">2910 East 57th Avenue #164<br>
                                    Spokane, Washington 99223
                                </div>

                                <b-alert show variant="success" class="mt-4"><strong><i class="fas fa-user-headset mr-2"></i>Need some help?</strong> Please <a href="mailto:help@thepaymentoption.com" style="text-decoration: underline; color: #143b31;">contact us</a> directly if you have any questions about your agreement. For questions about the merchant's products or services you can reach out to them.</b-alert>
                            </td>
                        </tr>
                        <tr>
                            <th>Account Number</th>
                            <td>R{{ account.id }}</td>
                        </tr>
                        <tr>
                            <th>Nickname</th>
                            <td>{{ account.nickname }} <i class="fas fa-pencil ml-2" v-b-modal.nickname></i></td>
                        </tr>
                        <tr v-if="account.agreement">
                            <th>Agreement</th>
                            <td><a class="btn btn-primary btn-sm" target="_blank" :href="'https://app.thepaymentoption.com/api/account/agreement/' + code + '/' + id"><i class="far fa-file-pdf mr-1"></i> View Agreement</a></td>
                        </tr>
                        <tr>
                            <th>Agreement Total</th>
                            <td>{{ account.agreement_total | currency }}</td>
                        </tr>
                        <tr>
                            <th>Interest Incurred</th>
                            <td style="vertical-align: top">{{ account.interest_incurred | currency }}</td>
                        </tr>
                        <tr>
                            <th>Fees Incurred</th>
                            <td style="vertical-align: top">{{ account.fees_incurred | currency }}</td>
                        </tr>
                        <tr>
                            <th>Total Due</th>
                            <td>{{ account.total | currency }}</td>
                        </tr>
                        <tr>
                            <th>Term</th>
                            <td>{{ account.term }} Months</td>
                        </tr>
                        <tr>
                            <th>Monthly</th>
                            <td>{{ account.monthly | currency }}</td>
                        </tr>
                        <tr>
                            <th>Payment Date</th>
                            <td>1st of Month</td>
                        </tr>
                        <tr>
                            <th>First Payment</th>
                            <td>
                                <div v-if="account.defer_on">{{ account.defer_on | moment('MMMM Do, YYYY') }}</div>
                                <div class="text-muted" v-else>N/A</div>
                            </td>
                        </tr>
                        <tr>
                            <th>Down Payment</th>
                            <td>{{ account.down | currency }}</td>
                        </tr>
                        <tr>
                            <th>Total Paid</th>
                            <td>{{ account.paid | currency }}</td>
                        </tr>
                        <tr>
                            <th>Payments Made</th>
                            <td>{{ account.payments_made }}</td>
                        </tr>
                        <tr>
                            <th>Total Remaining</th>
                            <td>{{ account.total - account.paid | currency }}</td>
                        </tr>
                        <tr>
                            <th>Last Attempt</th>
                            <td>
                                <div v-if="account.last_attempted_at">{{ account.last_attempted_at | moment('MMMM Do, YYYY') }}</div>
                                <div class="text-muted" v-else>N/A</div>
                            </td>
                        </tr>
                        <tr>
                            <th>Last Successful</th>
                            <td>
                                <div v-if="account.last_successful_at">{{ account.last_successful_at | moment('MMMM Do, YYYY') }}</div>
                                <div class="text-muted" v-else>N/A</div>
                            </td>
                        </tr>
                        <tr>
                            <th>Created</th>
                            <td>
                                <div v-if="account.created_at">{{ account.created_at | moment('MMMM Do, YYYY') }}</div>
                                <div class="text-muted" v-else>N/A</div>
                            </td>
                        </tr>
                        <tr>
                            <th>Updated</th>
                            <td>
                                <div v-if="account.updated_at">{{ account.updated_at | moment('MMMM Do, YYYY') }}</div>
                                <div class="text-muted" v-else>N/A</div>
                            </td>
                        </tr>
                    </table>

                </b-tab>
                <b-tab no-body>
                    <template v-slot:title>
                        <i class="fad fa-money-check-alt mr-2"></i>Payment Method
                        <i class="fas fa-exclamation-triangle ml-2" style="color: orangered" v-if="account.id && account.total > 0 && (account.total - account.paid) > 0 && (!account.payment_method.nickname || account.method_request)"></i>
                    </template>
                    <template v-if="account.payment_method && account.payment_method.nickname && account.paid < account.total">
                        <table class="details-table mt-3">
                            <tr>
                                <th class="no-border">Payment Method</th>
                                <td class="no-border">ACH Debit</td>
                            </tr>
                            <tr>
                                <th>Nickname</th>
                                <td>{{ account.payment_method.nickname }}</td>
                            </tr>
                            <tr>
                                <th>Account</th>
                                <td>{{ account.payment_method.type | capitalize }} ({{ account.payment_method.last_four }})</td>
                            </tr>
                            <tr>
                                <th>UPDATED</th>
                                <td>{{ account.payment_method.created_at | moment('MMM Do, YYYY') }}</td>
                            </tr>
                        </table>
                    </template>
                    <div v-else-if="account.id && account.total > 0 && (account.total - account.paid) <= 0" class="pt-5 pl-4">
                        <i class="far fa-info-circle mr-2"></i>Agreement is complete. No payment method is required.
                    </div>
                    <div v-else class="pt-5 pl-4">
                        <i class="fas fa-exclamation-triangle mr-2" style="color: orangered"></i>No payment method is on file.
                    </div>

                    <b-alert show variant="danger" v-if="account.method_request" class="mb-5 mt-3">
                        <div class="mb-3 mt-3">
                            <strong>ACTION REQUIRED</strong>
                        </div>
                        <p>You requested your payment method to be updated to account <strong>{{ account.method_request.nickname }} ({{ account.method_request.account }})</strong>.<br>For security purposes, we ask that you confirm this change by e-mail<span v-if="account.user.sms"> or by phone text-message</span>.</p>
                        <p>Your Payment Method <strong>WILL NOT</strong> be updated until the request is approved.</p>

                        <template v-if="!sms.requested">
                            <p><strong>Please <u>check your e-mail</u> to approve this request.</strong></p>

                            <p class="pt-2">
                                <b-button class="mt-2" variant="success" @click="doMethodSms"><i class="far fa-mobile mr-2"></i>Confirm by Phone Instead</b-button>
                            </p>
                        </template>

                        <template v-else>
                            <hr>
                            <p>
                                <i class="fas fa-arrow-right mr-2"></i>A 5 digit verification code has been sent by SMS text-message to your phone ({{ account.user.sms }}).
                            </p>

                            <b-form-group label="Verification Code">
                                <v-otp-input
                                    ref="otpInput"
                                    input-classes="otp-input"
                                    separator="-"
                                    :num-inputs="5"
                                    :should-auto-focus="true"
                                    :is-input-num="true"
                                    @on-change="handleOnChange"
                                    @on-complete="handleOnComplete"
                                />
                            </b-form-group>

                            <p v-if="sms.error"><i class="fas fa-exclamation-triangle mr-2"></i>The verification code you entered is not valid. Please check it or <a class="try-again" @click="sms.requested=false">start over</a>.</p>

                            <b-button class="mt-2 mb-3" variant="success" :disabled="!sms.code || sms.code.length < 5 || sms.processing" @click="doMethodSms"><i class="far fa-check mr-2"></i>Verify</b-button>
                        </template>
                    </b-alert>

                    <div style="padding: 1.25rem;" v-if="!methodUpdated">
                        <h5 class="border-bottom pb-1 mb-4 pt-4"><i class="far fa-money-check-edit-alt mr-3"></i>Update Payment Method</h5>

                        <p>Please enter your checking or savings account information to update your payment method.  This information can be found on a physical check (see image below), obtained from your bank, or often accessed from online banking tools or apps.  Please note that some banks use "sub-accounts" which are not supported.</p>
                        <p><i class="far fa-exclamation-triangle mr-2"></i>Your account number cannot be found on a credit or debit card.  Do not enter credit card information.</p>
                        <div class="mt-5">
                            <b-form-group label="Bank Routing Number">
                                <b-form-input required placeholder="Routing Number" v-model="newMethod.routing" maxlength="9" v-mask="'#########'"/>
                            </b-form-group>

                            <div class="text-danger mb-4" v-if="newMethod.routing.length === 9 && !validRouting"><i class="fas fa-exclamation-circle mr-2"></i>Invalid routing number, please double check it's accuracy.</div>

                            <b-form-group label="Account Number">
                                <b-form-input required placeholder="Account Number" v-model="newMethod.account" v-mask="'#################'"/>
                            </b-form-group>

                            <b-form-group label="Verify Account Number">
                                <b-form-input required placeholder="Account Number" v-model="newMethod.verifyAccount" v-mask="'#################'"/>
                            </b-form-group>

                            <b-form-group label="Account Type">
                                <b-form-select :options="types" required v-model="newMethod.type"/>
                            </b-form-group>

                            <b-form-group label="Payment Method Nickname">
                                <b-form-input length="200" required v-model="newMethod.nickname"/>
                            </b-form-group>

                            <b-button class="mt-3" variant="success" @click="doRequestMethod" :disabled="methodPending || !newMethod.routing || !validRouting || !newMethod.account || !newMethod.verifyAccount || !newMethod.nickname"><i class="far fa-check mr-2"></i>Submit</b-button>
                        </div>

                        <div class="text-center border-top mt-5">
                            <img src="@/assets/echeck.gif" class="mt-4" style="max-width: 100%">
                        </div>
                    </div>

                </b-tab>
            </b-tabs>

        </b-card>
        <div v-else>
            <i class="far fa-spin fa-spinner mr-3"></i>Loading account details. Please wait...
        </div>

        <b-modal id="pay" size="lg">
            <template #modal-title><i class="fad fa-money-check-edit fa-flip-horizontal mr-3 ml-1"></i>Make Additional Payment</template>

            <b-alert show variant="danger" class="m-3 mb-0" v-if="hasAutomaticPaymentDue">
                <h6 class="mb-2"><i class="fas fa-exclamation-triangle mr-2"></i>An Automatic Payment of {{ account.queued_payment_amount | currency }} is {{ account.has_queued_payment === 1 ? 'Pending' : 'Scheduled for ' + $options.filters.moment(account.next_charge_on, 'MMMM Do') }}</h6>
                <p class="mb-0">
                    That installment will be taken from your payment account automatically. <strong>A payment made here is in addition to it and WILL NOT replace it.</strong>
                    If you only want to make your regular monthly payment, you do not need to do anything.
                </p>
            </b-alert>

            <!--            <div class="p-3"><i class="fas fa-ban mr-2 ml-1 text-muted"></i>Additional payments are currently not available for this account.</div>-->

            <b-form class="m-3" v-if="!paymentRequested">
                <div class="mb-4"><strong>How much would you like to pay on this account?</strong></div>
                <div class="mb-1" style="font-size: 14px;"><strong>Remaining Balance: </strong>{{ account.total - account.paid | currency }}</div>
                <div class="mb-3" style="font-size: 14px;"><strong>Payment Account: </strong>{{ account.payment_method.nickname }} ({{ account.payment_method.last_four }})</div>
                <b-form-group>
                    <b-form-input required placeholder="$0.00" v-model="additionalPayment.amount" v-mask="paymentMask"/>
                </b-form-group>
                <div class="text-danger mt-2 mb-4" v-if="parseFloat(additionalPayment.amount.replace(/[^0-9.]/g, '')) > (account.total - account.paid)"><i class="fas fa-exclamation-circle mr-2"></i>Payment amount cannot exceed the account's remaining balance of {{ account.total - account.paid | currency }}.</div>

                <b-form-group>
                    <b-form-checkbox v-model="additionalPayment.agree">I understand that this is an additional payment to this account and WILL NOT stop or take the place of automatic payments which are due unless I pay off the entire balance.</b-form-checkbox>
                </b-form-group>

                <div class="mb-2 mt-4" style="font-size: 14px;"><strong>Please Note: </strong>Additional payments using ACH Debit will be submitted immediately after approval and cannot be cancelled or voided. It may take 5-7 business days to process your payment.</div>
            </b-form>
            <b-alert show variant="danger" v-else>
                <div v-if="this.requestPending">
                    <div class="mb-3">
                        <b-badge variant="danger">ACTION REQUIRED</b-badge>
                    </div>

                    <template v-if="!sms.requested">
                        <h6 class="text-dark">Please <u>check your e-mail</u> to approve this request.</h6>
                        <p>This additional payment <strong>WILL NOT</strong> process until the request is approved.</p>

                        <p class="pt-2">
                            <b-button class="mt-2" variant="success" @click="doRequestPaymentSms"><i class="far fa-mobile mr-2"></i>Confirm by Phone Instead</b-button>
                        </p>
                    </template>

                    <template v-else>
                        <hr>
                        <p>
                            <i class="fas fa-arrow-right mr-2"></i>A 5 digit verification code has been sent by SMS text-message to your phone ({{ account.user.sms }}).
                        </p>

                        <b-form-group label="Verification Code">
                            <v-otp-input
                                ref="otpInput"
                                input-classes="otp-input"
                                separator="-"
                                :num-inputs="5"
                                :should-auto-focus="true"
                                :is-input-num="true"
                                @on-change="handleOnChange"
                                @on-complete="handleOnComplete"
                            />
                        </b-form-group>

                        <p v-if="sms.error"><i class="fas fa-exclamation-triangle mr-2"></i>The verification code you entered is not valid. Please check it or <a class="try-again" @click="sms.requested=false">start over</a>.</p>

                        <b-button class="mt-2 mb-3" variant="success" :disabled="!sms.code || sms.code.length < 5 || sms.processing" @click="doValidateSms"><i class="far fa-check mr-2"></i>Verify</b-button>
                    </template>
                </div>
                <div v-else>
                    <h6 class="text-dark">Thank you. Your additional payment request has been submitted.</h6>
                    <p class="mb-0 mt-2" v-if="hasAutomaticPaymentDue">
                        Your automatic payment of <strong>{{ account.queued_payment_amount | currency }}</strong>
                        {{ account.has_queued_payment === 1 ? 'currently being processed' : 'scheduled for ' + $options.filters.moment(account.next_charge_on, 'MMMM Do') }}
                        is unchanged and will still be taken.
                    </p>
                </div>
            </b-alert>

            <template #modal-footer>
                <template v-if="!paymentRequested">
                    <b-button @click="$bvModal.hide('pay')">Cancel</b-button>
                    <b-button
                        @click="doRequestPayment()"
                        variant="success"
                        :disabled="!additionalPayment.agree
                                || requestPending
                                || !additionalPayment.amount
                                || additionalPayment.amount === '$'
                                || parseFloat(additionalPayment.amount.replace(/[^0-9.]/g, '')) <= 0
                                || parseFloat(additionalPayment.amount.replace(/[^0-9.]/g, '')) > (account.total - account.paid)
                    "><i class="far fa-check mr-2"></i>Submit Request
                    </b-button>
                </template>
                <template v-else>
                    <b-button variant="success" @click="$bvModal.hide('pay'); paymentRequested = false">Close</b-button>
                </template>
            </template>
        </b-modal>

        <b-modal id="nickname">
            <template #modal-title><i class="far fa-money-check-alt mr-2"></i>Account Nickname</template>

            <b-form>
                <b-form-group label="What would you like to call this account?">
                    <b-form-input required placeholder="Account Nickname" v-model="newNickname"/>
                </b-form-group>
            </b-form>

            <template #modal-footer>
                <b-button @click="$bvModal.hide('nickname')">Cancel</b-button>
                <b-button variant="success" @click="doUpdateName(); $bvModal.hide('nickname')"><i class="far fa-check mr-2"></i>Submit</b-button>
            </template>
        </b-modal>

        <b-modal id="payCreditCard" size="lg" @hidden="resetCreditCardPayment">
            <template #modal-title><i class="fas fa-credit-card mr-3"></i>Make Additional Payment by Credit Card</template>

            <b-alert show variant="info" class="mb-3">
                <p class="mb-0"><strong>Limited Time Offer:</strong> Until October 1, 2025, pay down your balance by credit cardwith only a 3% processing fee. Use the standard 'Make Additional Payment' button to pay by ACH Debit with no additional fee.</p>
            </b-alert>

            <!-- Step 1: Amount Selection -->
            <div v-show="creditCardPayment.step === 1 && !creditCardPayment.processing && !creditCardPayment.success">
                <b-form>
                    <div class="mb-3">
                        <strong>Remaining Balance: </strong>{{ (account.total - account.paid) | currency }}
                    </div>

                    <b-form-group label="Payment Amount" description="Enter the amount you wish to pay towards your balance">
                        <b-input-group>
                            <b-form-input
                                v-model="creditCardPayment.amount"
                                v-mask="paymentMask"
                                placeholder="$0.00"
                                :state="creditCardPayment.amount && parseFloat(creditCardPayment.amount.replace(/[^0-9.]/g, '')) > 0 && parseFloat(creditCardPayment.amount.replace(/[^0-9.]/g, '')) <= (account.total - account.paid) ? null : false"
                            />
                        </b-input-group>
                        <div class="text-danger mt-2" v-if="creditCardPayment.amount && parseFloat(creditCardPayment.amount.replace(/[^0-9.]/g, '')) > (account.total - account.paid)">
                            <i class="fas fa-exclamation-circle mr-2"></i>Amount cannot exceed your remaining balance
                        </div>
                    </b-form-group>

                    <div class="border rounded p-3 mb-3" v-if="creditCardPayment.amount && parseFloat(creditCardPayment.amount.replace(/[^0-9.]/g, '')) > 0 && parseFloat(creditCardPayment.amount.replace(/[^0-9.]/g, '')) <= (account.total - account.paid)">
                        <div><strong>Payment Amount:</strong> {{ parseFloat(creditCardPayment.amount.replace(/[^0-9.]/g, '')) | currency }}</div>
                        <div><strong>Processing Fee (3%):</strong> {{ calculateProcessingFee | currency }}</div>
                        <hr>
                        <div class="h5"><strong>Total Charge:</strong> {{ calculateTotalCharge | currency }}</div>
                    </div>
                </b-form>
            </div>

            <!-- Step 2: Payment Form -->
            <div v-show="creditCardPayment.step === 2 && !creditCardPayment.processing && !creditCardPayment.success">
                <b-form>
                    <div class="border rounded p-3 mb-3 bg-light">
                        <div><strong>Payment Amount:</strong> {{ parseFloat(creditCardPayment.amount.replace(/[^0-9.]/g, '')) | currency }}</div>
                        <div><strong>Processing Fee (3%):</strong> {{ calculateProcessingFee | currency }}</div>
                        <hr class="my-2">
                        <div class="h5 mb-0"><strong>Total Charge:</strong> {{ calculateTotalCharge | currency }}</div>
                    </div>

                    <b-form-group label="Payment Details">
                        <div id="card-element" ref="cardElement" style="padding: 0; min-height: 200px;">
                            <!-- Stripe Payment Element will be mounted here -->
                        </div>
                        <div v-if="creditCardPayment.cardError" class="text-danger mt-2">
                            <i class="fas fa-exclamation-circle mr-2"></i>{{ creditCardPayment.cardError }}
                        </div>
                    </b-form-group>

                    <b-form-group class="mt-4">
                        <b-form-checkbox v-model="creditCardPayment.agree">
                            I authorize The Payment Option to charge my selected payment method <strong>{{ calculateTotalCharge | currency }}</strong> ({{ parseFloat(creditCardPayment.amount.replace(/[^0-9.]/g, '')) | currency }} + 3% processing fee) as an additional payment to account R{{ account.id }}. I understand that this is a one-time payment and will not affect automatic payments which are due unless I pay off the entire balance.
                        </b-form-checkbox>
                    </b-form-group>
                </b-form>
            </div>

            <!-- Loading State -->
            <div v-show="creditCardPayment.processing" class="text-center py-5">
                <b-spinner variant="primary" label="Processing..."></b-spinner>
                <p class="mt-3">{{ creditCardPayment.loadingMessage }}</p>
            </div>

            <!-- Success State -->
            <div v-show="creditCardPayment.success" class="text-center py-5">
                <i class="fas fa-check-circle text-success" style="font-size: 4rem;"></i>
                <h5 class="mt-3">Payment Successful!</h5>
                <p>Your payment of {{ calculateTotalCharge | currency }} has been processed successfully.</p>
            </div>

            <template #modal-footer>
                <!-- Step 1 Footer -->
                <template v-if="creditCardPayment.step === 1 && !creditCardPayment.processing && !creditCardPayment.success">
                    <b-button @click="$bvModal.hide('payCreditCard')">Cancel</b-button>
                    <b-button
                        variant="primary"
                        @click="confirmAmountAndCreateIntent"
                        :disabled="!creditCardPayment.amount ||
                                   parseFloat(creditCardPayment.amount.replace(/[^0-9.]/g, '')) <= 0 ||
                                   parseFloat(creditCardPayment.amount.replace(/[^0-9.]/g, '')) > (account.total - account.paid)"
                    >
                        <i class="fas fa-arrow-right mr-2"></i>Confirm Amount
                    </b-button>
                </template>

                <!-- Step 2 Footer -->
                <template v-else-if="creditCardPayment.step === 2 && !creditCardPayment.processing && !creditCardPayment.success">
                    <b-button
                        variant="success"
                        @click="processCreditCardPayment"
                        :disabled="!creditCardPayment.agree"
                    >
                        <i class="fas fa-lock mr-2"></i>Pay {{ calculateTotalCharge | currency }}
                    </b-button>
                </template>

                <!-- Success Footer -->
                <template v-else-if="creditCardPayment.success">
                    <b-button variant="success" @click="$bvModal.hide('payCreditCard')">Close</b-button>
                </template>
            </template>
        </b-modal>

        <b-modal id="paySalesTax" size="lg" @hidden="resetTaxPayment">
            <template #modal-title><i class="fas fa-file-invoice-dollar mr-3"></i>Washington State Sales Tax</template>

            <!-- STATE 1: Pending plan exists -->
            <div v-if="taxPlanStatus && taxPlanStatus.hasPendingPlan && !taxPlanCreated">
                <b-alert show variant="info">
                    <h6 class="mb-2"><i class="fas fa-info-circle mr-2"></i>Pending Tax Payment Plan</h6>
                    <p>You already have a tax payment plan that needs to be signed.</p>
                </b-alert>
                <div class="border rounded p-3 bg-light">
                    <table class="table table-sm mb-0">
                        <tbody>
                            <tr>
                                <td><strong>Term:</strong></td>
                                <td>{{ taxPlanStatus.pendingPlan.term }} months</td>
                            </tr>
                            <tr>
                                <td><strong>Monthly Payment:</strong></td>
                                <td>{{ taxPlanStatus.pendingPlan.monthly | currency }}</td>
                            </tr>
                            <tr>
                                <td><strong>Total:</strong></td>
                                <td>{{ taxPlanStatus.pendingPlan.total | currency }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- STATE 3: Plan created - redirect -->
            <div v-else-if="taxPlanCreated" class="text-center py-5">
                <i class="fas fa-check-circle text-success" style="font-size: 4rem;"></i>
                <h5 class="mt-3">Payment Plan Created!</h5>
                <p>Your tax payment plan has been created. You will also receive an email and text message with the signing link.</p>
                <p class="text-muted">Term: {{ taxPlanCreated.term }} months &middot; Monthly: {{ taxPlanCreated.monthly | currency }}</p>
            </div>

            <!-- STATE 2: Choose payment method -->
            <div v-else>
                <!-- Shared: Address + Tax Details (visible on step 1, not during processing/success/confirming/creating) -->
                <div v-show="!taxPayment.processing && !taxPayment.success && taxPayment.step === 1 && !taxPlanConfirming && !taxPlanCreating">
                    <h6 class="mb-3">Address</h6>
                    <div class="border rounded p-3 mb-4 bg-light" v-if="taxInfo.address">
                        <div>{{ taxInfo.address.street }}</div>
                        <div>{{ taxInfo.address.city }}, {{ taxInfo.address.state }} {{ taxInfo.address.zip }}</div>
                    </div>

                    <h6 class="mb-3">Tax Calculation Details</h6>
                    <div class="border rounded mb-4 bg-light">
                        <table class="table table-sm tax-details-table mb-0">
                            <tbody>
                                <tr v-if="taxInfo.jurisdiction">
                                    <td><strong>Tax Jurisdiction:</strong></td>
                                    <td>{{ taxInfo.jurisdiction }}</td>
                                </tr>
                                <tr v-if="taxInfo.stateRate">
                                    <td><strong>Washington State Tax:</strong></td>
                                    <td>{{ (taxInfo.stateRate * 100).toFixed(3) }}%</td>
                                </tr>
                                <tr v-if="taxInfo.localRate">
                                    <td><strong>Local Tax:</strong></td>
                                    <td>{{ (taxInfo.localRate * 100).toFixed(3) }}%</td>
                                </tr>
                                <tr v-if="taxInfo.taxRate">
                                    <td><strong>Total Tax Rate:</strong></td>
                                    <td>{{ (taxInfo.taxRate * 100).toFixed(3) }}%</td>
                                </tr>
                                <tr v-if="taxInfo.unpaidTaxAmount > 0" class="border-top">
                                    <td><strong>Sales Tax Due:</strong></td>
                                    <td><strong>{{ taxInfo.unpaidTaxAmount | currency }}</strong></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <b-alert show variant="info" class="mb-4">
                        Sales tax has been calculated based on your balance as of October 1, 2025. Additional taxes may be required in event of default or other fees.
                    </b-alert>

                    <!-- Toggle: Pay in Full vs Payment Plan -->
                    <h6 class="mb-3">How would you like to handle your sales tax?</h6>
                    <div class="row mb-4">
                        <div class="col-md-6 mb-2">
                            <div class="card h-100" :class="{ 'border-primary shadow-sm': taxPlanMode === 'pay' }" @click="taxPlanMode = 'pay'" style="cursor: pointer;">
                                <div class="card-body text-center py-4">
                                    <i class="fas fa-credit-card fa-2x mb-2" :class="taxPlanMode === 'pay' ? 'text-primary' : 'text-muted'"></i>
                                    <h6 class="mb-1" :class="{ 'text-primary': taxPlanMode === 'pay' }">Pay in Full</h6>
                                    <small class="text-muted">One-time credit card payment</small>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-6 mb-2">
                            <div class="card h-100" :class="{ 'border-primary shadow-sm': taxPlanMode === 'plan' }" @click="taxPlanMode = 'plan'" style="cursor: pointer;">
                                <div class="card-body text-center py-4">
                                    <i class="fas fa-calendar-alt fa-2x mb-2" :class="taxPlanMode === 'plan' ? 'text-primary' : 'text-muted'"></i>
                                    <h6 class="mb-1" :class="{ 'text-primary': taxPlanMode === 'plan' }">Set Up a Payment Plan</h6>
                                    <small class="text-muted">3, 5, or 10 monthly payments</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- PAY IN FULL: Credit card payment form (step 2 only) -->
                <div v-show="taxPlanMode === 'pay' && !taxPayment.processing && !taxPayment.success">
                    <div v-if="taxPayment.step === 2">
                        <h6 class="mb-3">Payment Information</h6>

                        <b-form-group>
                            <div id="tax-card-element" ref="taxCardElement" style="padding: 0; min-height: 200px;">
                                <!-- Stripe Payment Element will be mounted here -->
                            </div>
                            <div v-if="taxPayment.cardError" class="text-danger mt-2">
                                <i class="fas fa-exclamation-circle mr-2"></i>{{ taxPayment.cardError }}
                            </div>
                        </b-form-group>

                        <hr/>

                        <b-form-group class="mt-5">
                            <b-form-checkbox v-model="taxPayment.agree">
                                I authorize The Payment Option to charge my selected payment method <strong>{{ taxInfo.unpaidTaxAmount | currency }}</strong> as payment for Washington State sales tax on account R{{ account.id }}. I understand this is a one-time payment for sales tax only.
                            </b-form-checkbox>
                        </b-form-group>
                    </div>
                </div>

                <!-- PAYMENT PLAN: Term selection (shown below toggle when plan mode selected) -->
                <div v-show="taxPlanMode === 'plan' && !taxPlanConfirming && !taxPlanCreating && taxPayment.step === 1">
                    <h6 class="mb-3">Choose a payment plan term.</h6>

                    <div class="row">
                        <div class="col-md-4 mb-3" v-for="option in planOptions" :key="option.term">
                            <div class="card h-100 text-center">
                                <div class="card-body">
                                    <h5 class="card-title text-primary">{{ option.term }} Months</h5>
                                    <div class="mb-2">
                                        <span class="h4">{{ option.monthly | currency }}</span>
                                        <small class="text-muted d-block">per month</small>
                                    </div>
                                    <hr>
                                    <p class="small mb-1">Fee: {{ option.fee | currency }}</p>
                                    <p class="small mb-2">Total: {{ option.total | currency }}</p>
                                    <p class="small text-muted mb-3">First Payment: {{ defermentDate | moment('MMM D, YYYY') }}</p>
                                    <b-button variant="primary" size="sm" @click="selectTaxPlanTerm(option.term)">
                                        Select
                                    </b-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- PAYMENT PLAN: Confirmation view -->
                <div v-if="taxPlanConfirming && !taxPlanCreating && !taxPlanCreated">
                    <h6 class="mb-3">Confirm Your Payment Plan</h6>
                    <div class="border rounded p-3 mb-4 bg-light" v-if="selectedTerm">
                        <table class="table table-sm mb-0">
                            <tbody>
                                <tr>
                                    <td><strong>Tax Balance:</strong></td>
                                    <td>{{ unpaidTaxAmount | currency }}</td>
                                </tr>
                                <template v-for="option in planOptions">
                                    <template v-if="option.term === selectedTerm">
                                        <tr :key="'fee-' + option.term">
                                            <td><strong>Fee:</strong></td>
                                            <td>{{ option.fee | currency }}</td>
                                        </tr>
                                        <tr :key="'total-' + option.term">
                                            <td><strong>Total:</strong></td>
                                            <td>{{ option.total | currency }}</td>
                                        </tr>
                                        <tr :key="'term-' + option.term">
                                            <td><strong>Term:</strong></td>
                                            <td>{{ option.term }} months</td>
                                        </tr>
                                        <tr :key="'monthly-' + option.term">
                                            <td><strong>Monthly Payment:</strong></td>
                                            <td>{{ option.monthly | currency }}</td>
                                        </tr>
                                    </template>
                                </template>
                                <tr>
                                    <td><strong>First Payment:</strong></td>
                                    <td>{{ defermentDate | moment('MMMM Do, YYYY') }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <b-alert show variant="info">
                        After creating this plan, you will need to sign an agreement with The Payment Option and provide a bank account for automatic monthly payments.
                    </b-alert>
                    <b-alert show variant="danger" v-if="taxPlanError">
                        <i class="fas fa-exclamation-circle mr-2"></i>{{ taxPlanError }}
                    </b-alert>
                </div>

                <!-- PAYMENT PLAN: Creating spinner -->
                <div v-if="taxPlanCreating" class="text-center py-5">
                    <b-spinner variant="primary" label="Creating..."></b-spinner>
                    <p class="mt-3">Creating your payment plan...</p>
                </div>

                <!-- Loading State (credit card) -->
                <div v-show="taxPlanMode === 'pay' && taxPayment.processing" class="text-center py-5">
                    <b-spinner variant="primary" label="Processing..."></b-spinner>
                    <p class="mt-3">{{ taxPayment.loadingMessage }}</p>
                </div>

                <!-- Success State (credit card) -->
                <div v-show="taxPlanMode === 'pay' && taxPayment.success" class="text-center py-5">
                    <i class="fas fa-check-circle text-success" style="font-size: 4rem;"></i>
                    <h5 class="mt-3">Tax Payment Successful!</h5>
                    <p>Your sales tax payment has been processed successfully.</p>
                </div>
            </div>

            <template #modal-footer>
                <!-- STATE 1: Pending plan footer -->
                <template v-if="taxPlanStatus && taxPlanStatus.hasPendingPlan && !taxPlanCreated">
                    <b-button @click="$bvModal.hide('paySalesTax')">Close</b-button>
                    <b-button variant="primary" @click="goToSignAgreement(taxPlanStatus.pendingPlan.hash)">
                        <i class="fas fa-signature mr-2"></i>Sign Agreement
                    </b-button>
                </template>

                <!-- STATE 3: Plan created footer -->
                <template v-else-if="taxPlanCreated">
                    <b-button @click="$bvModal.hide('paySalesTax')">Close</b-button>
                    <b-button variant="success" @click="goToSignAgreement(taxPlanCreated.hash)">
                        <i class="fas fa-signature mr-2"></i>Sign Your Agreement
                    </b-button>
                </template>

                <!-- Credit card: Step 1 footer -->
                <template v-else-if="taxPlanMode === 'pay' && taxPayment.step === 1 && !taxPayment.processing && !taxPayment.success">
                    <b-button @click="$bvModal.hide('paySalesTax')">Close</b-button>
                    <b-button
                        variant="danger"
                        @click="proceedToPayment"
                        :disabled="!taxInfo.unpaidTaxAmount || taxInfo.unpaidTaxAmount <= 0"
                    >
                        <i class="fas fa-credit-card mr-2"></i>Pay in Full by Credit Card
                    </b-button>
                </template>

                <!-- Credit card: Step 2 footer -->
                <template v-else-if="taxPlanMode === 'pay' && taxPayment.step === 2 && !taxPayment.processing && !taxPayment.success">
                    <b-button @click="taxPayment.step = 1">
                        <i class="fas fa-arrow-left mr-2"></i>Back
                    </b-button>
                    <b-button
                        variant="danger"
                        @click="processTaxPayment"
                        :disabled="!taxPayment.agree"
                    >
                        <i class="fas fa-lock mr-2"></i>Pay {{ taxInfo.unpaidTaxAmount | currency }}
                    </b-button>
                </template>

                <!-- Credit card: Success footer -->
                <template v-else-if="taxPlanMode === 'pay' && taxPayment.success">
                    <b-button variant="success" @click="$bvModal.hide('paySalesTax')">Close</b-button>
                </template>

                <!-- Payment plan: Confirming footer -->
                <template v-else-if="taxPlanMode === 'plan' && taxPlanConfirming && !taxPlanCreating">
                    <b-button @click="taxPlanConfirming = false; selectedTerm = null; taxPlanError = null;">
                        <i class="fas fa-arrow-left mr-2"></i>Back
                    </b-button>
                    <b-button variant="primary" @click="createTaxPlan">
                        <i class="fas fa-check mr-2"></i>Create Payment Plan
                    </b-button>
                </template>

                <!-- Payment plan: Default (term selection) footer -->
                <template v-else-if="taxPlanMode === 'plan'">
                    <b-button @click="$bvModal.hide('paySalesTax')">Close</b-button>
                </template>

                <!-- Fallback -->
                <template v-else>
                    <b-button @click="$bvModal.hide('paySalesTax')">Close</b-button>
                </template>
            </template>
        </b-modal>
    </div>
</template>

<script>
    import Copyright from "../components/Copyright";
    import {mapActions} from "vuex";
    import axios from "axios";
    import {apiUrl, stripePublicKey} from "@/constants/config";
    import createNumberMask from 'text-mask-addons/dist/createNumberMask';

    const numberMask = createNumberMask({
        prefix: '$',
        suffix: '',
        allowDecimal: true,
        decimalLimit: 2,
        integerLimit: 4,
    });

    export default {
        name: "account",
        components: {Copyright},
        props: ['id', 'code'],
        data: function() {
            return {
                account: {
                    total: 0,
                    payment_method: {
                        nickname: '',
                        last_four: ''
                    },
                    transactions: [],
                    is_error: 0
                },
                fields: [
                    {key: 'created_at', label: 'DATE'},
                    {key: 'title', label: 'DESCRIPTION'},
                    {key: 'result', label: 'RESULT'},
                    {key: 'total', label: 'AMOUNT'},
                ],
                types: [
                    {text: 'Checking', value: 'checking'},
                    {text: 'Savings', value: 'savings'}
                ],
                newMethod: {
                    routing: '',
                    account: null,
                    verifyAccount: null,
                    type: 1,
                    nickname: '',
                    user_id: null,
                    customer_id: null
                },
                newNickname: '',
                methodPending: false,
                methodUpdated: null,
                additionalPayment: {
                    amount: '',
                    agree: false
                },
                requestPending: false,
                paymentRequested: false,
                paymentMask: numberMask,
                sms: {
                    processing: false,
                    requested: false,
                    code: null,
                    verified: false,
                    error: false
                },
                creditCardPayment: {
                    step: 1,
                    amount: '',
                    agree: false,
                    processing: false,
                    success: false,
                    cardError: null,
                    stripe: null,
                    elements: null,
                    cardElement: null,
                    paymentIntentClientSecret: null,
                    loadingMessage: 'Processing...'
                },
                taxInfo: {
                    taxRate: 0,
                    stateRate: 0,
                    localRate: 0,
                    jurisdiction: null,
                    locationCode: null,
                    resultCode: null,
                    taxAmount: 0,
                    taxPaid: 0,
                    unpaidTaxAmount: 0,
                    taxCalculatedAt: null,
                    remainingBalance: 0,
                    address: null
                },
                taxPayment: {
                    step: 1,
                    agree: false,
                    processing: false,
                    success: false,
                    cardError: null,
                    stripe: null,
                    elements: null,
                    cardElement: null,
                    paymentIntentClientSecret: null,
                    loadingMessage: 'Processing...'
                },
                taxNoticeHidden: false,
                unpaidTaxAlertHidden: false,
                paymentNotificationHidden: false,
                upcomingNotificationHidden: false,
                pastDueNotificationHidden: false,
                defaultNotificationHidden: false,
                collectionsNotificationHidden: false,
                // Tax payment plan (self-service)
                taxPlanMode: 'pay',
                taxPlanStatus: null,
                selectedTerm: null,
                taxPlanConfirming: false,
                taxPlanCreating: false,
                taxPlanCreated: null,
                taxPlanError: null,
            };
        },
        computed: {
            showQueuedPaymentNotification: function() {
                if (this.paymentNotificationHidden) return false;
                if (this.account.paid >= this.account.total) return false;
                return this.account.has_queued_payment === 1;
            },
            showUpcomingPaymentNotification: function() {
                if (this.upcomingNotificationHidden) return false;
                if (this.account.paid >= this.account.total) return false;
                return this.account.has_upcoming_payment === 1;
            },
            // An automatic installment is either queued for today or coming up within a few days
            hasAutomaticPaymentDue: function() {
                if (!this.account.id || this.account.paid >= this.account.total) return false;
                return this.account.has_queued_payment === 1 || this.account.has_upcoming_payment === 1;
            },
            showPastDueNotification: function() {
                if (this.pastDueNotificationHidden) return false;
                if (this.account.paid >= this.account.total) return false;
                // Only show if not in default/collections (they have their own alerts)
                return this.account.past_due_amount > 0
                    && this.account.is_error === 0
                    && this.account.is_default === 0
                    && this.account.is_collections === 0;
            },
            showDefaultNotification: function() {
                if (this.defaultNotificationHidden) return false;
                return this.account.is_default === 1;
            },
            showCollectionsNotification: function() {
                if (this.collectionsNotificationHidden) return false;
                return this.account.is_collections === 1;
            },
            unpaidTaxAmount: function() {
                return this.taxInfo.unpaidTaxAmount || 0;
            },
            planOptions: function() {
                const taxBalance = this.unpaidTaxAmount;
                if (taxBalance <= 0) return [];
                const taxRate = this.taxInfo.taxRate || 0;
                const calculatedTax = Math.ceil((taxBalance * taxRate) / 5) * 5;
                const flatFees = { 3: 25, 5: 40, 10: 70 };
                return [3, 5, 10].map(term => {
                    const fee = calculatedTax + flatFees[term];
                    const total = taxBalance + fee;
                    const monthly = Math.ceil(total / term);
                    return { term, fee, total, monthly };
                });
            },
            defermentDate: function() {
                const today = new Date();
                let deferDate;
                if (today.getDate() <= 21) {
                    deferDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
                } else {
                    deferDate = new Date(today.getFullYear(), today.getMonth() + 2, 1);
                }
                return deferDate;
            },
            validRouting: function() {
                let num = this.newMethod.routing;

                let n = 0;
                for (let i = 0; i < num.length; i += 3) {
                    n += parseInt(num.charAt(i), 10) * 3
                        + parseInt(num.charAt(i + 1), 10) * 7
                        + parseInt(num.charAt(i + 2), 10);
                }

                return n !== 0 && n % 10 === 0;
            },
            calculateProcessingFee: function() {
                const amount = parseFloat(this.creditCardPayment.amount.replace(/[^0-9.]/g, '') || 0);
                return amount * 0.03;
            },
            calculateTotalCharge: function() {
                const amount = parseFloat(this.creditCardPayment.amount.replace(/[^0-9.]/g, '') || 0);
                return amount + (amount * 0.03);
            }
        },
        methods: {
            ...mapActions(['getAccount', 'requestMethod', 'updateName']),
            loadAccount() {
                this.getAccount({code: this.code, id: this.id}).then(account => {
                    for (let i = 0; i < account.transactions.length; i++) {
                        account.transactions[i].expanded = false;
                    }
                    this.account = account;
                    this.newNickname = account.nickname;
                }, err => {
                    console.error(err);
                });
            },
            doRequestMethod: function() {
                if (!this.methodPending) {
                    this.methodPending = true;

                    if (!this.newMethod.account || this.newMethod.account !== this.newMethod.verifyAccount) {
                        window.alert('Invalid Account Number or it didn\'t match verification.');
                        this.methodPending = false;
                        return;
                    }
                    this.newMethod.customer_id = this.account.fatt_customer_id;
                    this.newMethod.user_id = this.account.user_id;
                    this.requestMethod({
                        code: this.code,
                        id: this.id,
                        ...this.newMethod
                    }).then(result => {
                        this.methodUpdated = {
                            nickname: this.newMethod.nickname,
                            last_four: this.newMethod.account.substr(-4),
                        };

                        console.log("result", result);
                        this.account.method_request = result;
                    });
                }
            },
            doUpdateName: function() {
                if (this.newNickname === '') this.newNickname = this.account.nickname;
                this.updateName({
                    code: this.code,
                    id: this.id,
                    nickname: this.newNickname
                }).then(result => {
                    this.account.nickname = this.newNickname;
                });
            },
            doResolveError: function() {
                axios.post(apiUrl + 'api/account/' + this.code + '/' + this.id + '/resolve', {}).then(response => {
                    this.$emit('resolve', this.id);
                    this.account.is_error = 0;
                    // Reload account to get updated queue status and other info
                    this.loadAccount();
                }, err => {
                    window.alert('A problem occurred resolving the error.  Please try again or contact us.');
                });
            },
            doRequestPayment: function() {
                if (!this.requestPending) {
                    this.requestPending = true;

                    axios.post(apiUrl + 'api/account/' + this.code + '/' + this.id + '/payment', {
                        amount: parseFloat(this.additionalPayment.amount.replace(/[^0-9.]/g, '')),
                        invoice_id: this.account.fatt_invoice_id,
                        method_id: this.account.payment_method.fatt_method_id
                    }).then(response => {
                        this.paymentRequested = true;
                        this.additionalPayment.request_id = response.data.request_id;
                        this.loadAccount();
                    }, err => {
                        if (err.response && err.response.status === 403) {
                            window.alert("This account is in default or collections, so payments can't be made online. No charge was made. Please contact us at help@thepaymentoption.com to make a payment.");
                            this.loadAccount();
                        } else {
                            window.alert('A problem occurred submitting this payment. Please contact us before continuing.');
                        }
                    });
                }
            },
            doMethodSms: function() {
                this.sms.error = false;
                this.sms.processing = true;
                this.sms.requested = true;
                axios.post(apiUrl + 'api/account/method_sms/', {id: this.account.id, request_id: this.account.method_request.request_id, code: this.sms.code}).then(response => {
                    this.sms.verified = response.data.verified;

                    if (response.data.verified) {
                        if (!this.account.payment_method) this.account.payment_method = {};
                        this.account.payment_method.last_four = this.account.method_request.account;
                        this.account.payment_method.nickname = this.account.method_request.nickname;
                        // todo type (checking/savings)
                        this.account.payment_method.fatt_method_id = null;
                        this.account.payment_method.created_at = new Date();
                        this.account.method_request = null;
                        this.requestPending = false;
                        // Reload account to get updated queue status and other info
                        this.loadAccount();
                    }
                    this.sms.processing = false;
                }, err => {
                    this.$refs.otpInput.clearInput();
                    this.sms.error = true;
                    this.sms.code = null;
                    this.sms.processing = false;
                });
            },
            doRequestPaymentSms: function() {
                if (this.requestPending && this.additionalPayment.request_id && this.paymentRequested) {
                    this.sms.requested = true;
                    this.sms.processing = true;
                    this.requestPending = true;
                    axios.post(apiUrl + 'api/account/' + this.code + '/' + this.id + '/payment_sms', {
                        request_id: this.additionalPayment.request_id
                    }).then(response => {
                        this.sms.processing = false;
                    }, err => {
                        this.sms.error = true;
                        this.sms.code = null;
                        this.sms.processing = false;
                        window.alert('A problem occurred sending the SMS code verification code. Make sure you have a phone number on your account, or contact us before continuing.');
                    });
                }
            },
            doValidateSms: function() {
                this.sms.processing = true;
                axios.post(apiUrl + 'api/account/approve_p_sms', {
                    code: this.sms.code
                }).then(response => {
                    this.sms.verified = response.data.verified;
                    this.sms.processing = false;
                    this.requestPending = false;
                    this.additionalPayment.request_id = '';
                    this.additionalPayment.agree = false;
                    this.additionalPayment.amount = '';
                    this.sms.requested = false;
                    this.loadAccount();
                }, err => {
                    this.sms.error = true;
                    this.sms.code = null;
                    this.sms.processing = false;
                    this.additionalPayment.agree = false;
                    this.additionalPayment.amount = '';
                    if (err.response && err.response.status === 403) {
                        this.requestPending = false;
                        this.additionalPayment.request_id = '';
                        this.sms.requested = false;
                        window.alert("This account is in default or collections, so payments can't be made online. No charge was made. Please contact us at help@thepaymentoption.com to make a payment.");
                        this.loadAccount();
                    } else if (err.response && err.response.status === 402 && err.response.data && err.response.data.reason) {
                        this.requestPending = false;
                        this.additionalPayment.request_id = '';
                        this.sms.requested = false;
                        window.alert('The payment was not processed. Your payment processor reported: ' + err.response.data.reason + '. No charge was made. Please try again or contact us for help.');
                        this.loadAccount();
                    } else {
                        window.alert('A problem occurred submitting this payment. Please contact us before continuing.');
                    }
                });
            },
            handleOnComplete(value) {
                this.sms.code = value;
            },
            handleOnChange(value) {
                this.sms.code = value;
            },
            initializeStripe() {
                if (window.Stripe && !this.creditCardPayment.stripe) {
                    this.creditCardPayment.stripe = window.Stripe(stripePublicKey);
                }
            },
            mountStripeElements() {
                // Always recreate the element when mounting to ensure it's fresh
                if (this.creditCardPayment.stripe) {
                    // Unmount existing element if it exists
                    if (this.creditCardPayment.cardElement) {
                        try {
                            this.creditCardPayment.cardElement.unmount();
                        } catch (e) {
                            console.log('Element unmount failed:', e);
                        }
                        this.creditCardPayment.cardElement = null;
                    }

                    this.creditCardPayment.elements = this.creditCardPayment.stripe.elements({
                        clientSecret: this.creditCardPayment.paymentIntentClientSecret
                    });

                    this.creditCardPayment.cardElement = this.creditCardPayment.elements.create('payment', {
                        layout: 'tabs',
                        fields: {
                            billingDetails: {
                                name: 'auto',
                                email: 'auto',
                                address: 'auto'
                            }
                        }
                    });

                    // Wait a bit longer and try multiple ways to mount
                    setTimeout(() => {
                        const cardElementDiv = document.getElementById('card-element');
                        if (cardElementDiv) {
                            try {
                                this.creditCardPayment.cardElement.mount('#card-element');

                                this.creditCardPayment.cardElement.on('ready', () => {
                                    console.log('Stripe Payment Element ready');
                                });

                                console.log('Stripe element mounted successfully');
                            } catch (error) {
                                console.error('Failed to mount Stripe element:', error);
                                this.creditCardPayment.cardError = 'Failed to initialize payment form. Please refresh and try again.';
                            }
                        } else {
                            console.error('Card element div not found');
                            this.creditCardPayment.cardError = 'Payment form container not found. Please refresh and try again.';
                        }
                    }, 100);
                }
            },
            async remountCardElement() {
                return new Promise((resolve) => {
                    // Wait for DOM to be ready and try to remount
                    setTimeout(() => {
                        this.mountStripeElements();
                        // Give it a moment to mount
                        setTimeout(() => {
                            resolve();
                        }, 200);
                    }, 100);
                });
            },
            async confirmAmountAndCreateIntent() {
                this.creditCardPayment.processing = true;
                this.creditCardPayment.loadingMessage = 'Contacting payment processor...';
                this.creditCardPayment.cardError = null;

                try {
                    const amount = parseFloat(this.creditCardPayment.amount.replace(/[^0-9.]/g, ''));
                    const totalAmount = Math.round((amount + (amount * 0.03)) * 100); // Convert to cents

                    // Create payment intent on the backend
                    const response = await axios.post(apiUrl + 'api/account/' + this.code + '/' + this.id + '/create-payment-intent', {
                        amount: totalAmount,
                        currency: 'usd',
                        metadata: {
                            account_id: this.account.id,
                            original_amount: amount,
                            processing_fee: amount * 0.03,
                        }
                    });

                    this.creditCardPayment.paymentIntentClientSecret = response.data.clientSecret;

                    // Initialize Stripe if not already done
                    this.initializeStripe();

                    this.creditCardPayment.step = 2;
                    this.creditCardPayment.processing = false;

                    // Mount Stripe Elements after moving to step 2
                    // Payment Element needs clientSecret before mounting
                    this.$nextTick(() => {
                        this.mountStripeElements();
                    });

                } catch (error) {
                    this.creditCardPayment.cardError = 'Failed to create payment intent. Please try again.';
                    this.creditCardPayment.processing = false;
                    console.error('Payment intent error:', error);
                }
            },
            async processCreditCardPayment() {
                this.creditCardPayment.processing = true;
                this.creditCardPayment.loadingMessage = 'Processing your payment...';
                this.creditCardPayment.cardError = null;

                try {
                    // Verify card element is mounted and ready
                    if (!this.creditCardPayment.cardElement) {
                        throw new Error('Card element not initialized. Please refresh and try again.');
                    }

                    // Check if the element is still mounted in the DOM
                    const cardElementDiv = document.getElementById('card-element');
                    if (!cardElementDiv || !cardElementDiv.firstChild) {
                        console.error('Card element not found in DOM, attempting to remount...');
                        await this.remountCardElement();

                        if (!this.creditCardPayment.cardElement) {
                            throw new Error('Unable to initialize payment form. Please refresh the page and try again.');
                        }
                    }

                    const amount = parseFloat(this.creditCardPayment.amount.replace(/[^0-9.]/g, ''));
                    const totalAmount = Math.round((amount + (amount * 0.03)) * 100); // Convert to cents

                    // Confirm the payment with Stripe using the stored client secret
                    const result = await this.creditCardPayment.stripe.confirmPayment({
                        elements: this.creditCardPayment.elements,
                        confirmParams: {
                            return_url: window.location.href, // Required but won't be used since we handle success in JS
                        },
                        redirect: 'if_required', // Only redirect if absolutely necessary (e.g., 3D Secure)
                    });

                    if (result.error) {
                        this.creditCardPayment.cardError = result.error.message;
                        this.creditCardPayment.processing = false;
                    } else {
                        // Payment succeeded
                        this.creditCardPayment.success = true;
                        this.creditCardPayment.processing = false;

                        // Record the payment in the backend
                        await axios.post(apiUrl + 'api/account/' + this.code + '/' + this.id + '/record-credit-payment', {
                            payment_intent_id: result.paymentIntent.id,
                            amount: amount,
                            processing_fee: amount * 0.03,
                            total_charged: totalAmount / 100,
                        });

                        // Add a small delay to ensure database transaction commits, then reload account
                        setTimeout(() => {
                            this.loadAccount();
                        }, 500);
                    }
                } catch (error) {
                    this.creditCardPayment.cardError = 'An error occurred processing your payment. Please try again.';
                    this.creditCardPayment.processing = false;
                    console.error('Payment error:', error);
                }
            },
            resetCreditCardPayment() {
                // Unmount the card element if it exists
                if (this.creditCardPayment.cardElement) {
                    try {
                        this.creditCardPayment.cardElement.unmount();
                    } catch (e) {
                        // Element might already be unmounted
                    }
                }

                // Keep the stripe instance but reset everything else
                const stripeInstance = this.creditCardPayment.stripe;

                this.creditCardPayment = {
                    step: 1,
                    amount: '',
                    agree: false,
                    processing: false,
                    success: false,
                    cardError: null,
                    stripe: stripeInstance,
                    elements: null,
                    cardElement: null,
                    paymentIntentClientSecret: null,
                    loadingMessage: 'Processing...'
                };

                // Initialize Stripe if needed
                if (!this.creditCardPayment.stripe) {
                    this.initializeStripe();
                }
            },
            async loadTaxInfo() {
                try {
                    const response = await axios.post(apiUrl + 'api/account/' + this.code + '/' + this.id + '/get-tax-info');
                    this.taxInfo = response.data;
                } catch (error) {
                    console.error('Failed to load tax info:', error);
                }
            },
            async openTaxPaymentModal() {
                // Load fresh tax info
                await this.loadTaxInfo();

                // Check for existing unsigned tax plan
                try {
                    const response = await axios.post(apiUrl + 'api/account/' + this.code + '/' + this.id + '/tax-plan-status');
                    this.taxPlanStatus = response.data;
                } catch (error) {
                    console.error('Failed to check tax plan status:', error);
                    this.taxPlanStatus = null;
                }

                // Open the modal
                this.$bvModal.show('paySalesTax');
            },
            selectTaxPlanTerm(term) {
                this.selectedTerm = term;
                this.taxPlanConfirming = true;
            },
            async createTaxPlan() {
                this.taxPlanCreating = true;
                this.taxPlanError = null;

                try {
                    const response = await axios.post(apiUrl + 'api/account/' + this.code + '/' + this.id + '/create-tax-plan', {
                        term: this.selectedTerm
                    });

                    this.taxPlanCreated = response.data;
                } catch (error) {
                    if (error.response && error.response.status === 409 && error.response.data.error === 'existing_plan') {
                        // Treat duplicate as success — show signing link
                        this.taxPlanCreated = {
                            hash: error.response.data.hash,
                            term: error.response.data.term,
                            monthly: error.response.data.monthly,
                            total: error.response.data.total,
                            fee: 0
                        };
                    } else {
                        this.taxPlanError = (error.response && error.response.data && error.response.data.error) || 'Failed to create payment plan. Please try again.';
                        console.error('Create tax plan error:', error);
                    }
                } finally {
                    this.taxPlanCreating = false;
                }
            },
            goToSignAgreement(hash) {
                window.location.href = '/#/sign-agreement/' + hash;
                window.location.reload();
            },
            async proceedToPayment() {
                this.taxPayment.processing = true;
                this.taxPayment.loadingMessage = 'Contacting payment processor...';
                this.taxPayment.cardError = null;

                try {
                    const taxAmountInCents = Math.round(this.taxInfo.unpaidTaxAmount * 100);

                    // Create payment intent
                    const intentResponse = await axios.post(apiUrl + 'api/account/' + this.code + '/' + this.id + '/pay-tax-intent', {
                        amount: taxAmountInCents
                    });

                    this.taxPayment.paymentIntentClientSecret = intentResponse.data.clientSecret;

                    // Initialize Stripe if not already done
                    if (!this.taxPayment.stripe) {
                        this.initializeTaxStripe();
                    }

                    this.taxPayment.step = 2;
                    this.taxPayment.processing = false;

                    // Mount Stripe Elements after moving to step 2
                    // Payment Element needs clientSecret before mounting
                    this.$nextTick(() => {
                        this.mountTaxStripeElements();
                    });
                } catch (error) {
                    this.taxPayment.cardError = 'Failed to create payment intent. Please try again.';
                    this.taxPayment.processing = false;
                    console.error('Tax payment intent error:', error);
                }
            },
            initializeTaxStripe() {
                if (window.Stripe && !this.taxPayment.stripe) {
                    this.taxPayment.stripe = window.Stripe(stripePublicKey);
                }
            },
            mountTaxStripeElements() {
                if (this.taxPayment.stripe) {
                    // Unmount existing element if it exists
                    if (this.taxPayment.cardElement) {
                        try {
                            this.taxPayment.cardElement.unmount();
                        } catch (e) {
                            console.log('Element unmount failed:', e);
                        }
                        this.taxPayment.cardElement = null;
                    }

                    this.taxPayment.elements = this.taxPayment.stripe.elements({
                        clientSecret: this.taxPayment.paymentIntentClientSecret
                    });

                    this.taxPayment.cardElement = this.taxPayment.elements.create('payment', {
                        layout: 'tabs',
                        fields: {
                            billingDetails: {
                                name: 'auto',
                                email: 'auto',
                                address: 'auto'
                            }
                        }
                    });

                    setTimeout(() => {
                        const cardElementDiv = document.getElementById('tax-card-element');
                        if (cardElementDiv) {
                            try {
                                this.taxPayment.cardElement.mount('#tax-card-element');

                                this.taxPayment.cardElement.on('ready', () => {
                                    console.log('Tax Stripe Payment Element ready');
                                });

                                console.log('Tax Stripe element mounted successfully');
                            } catch (error) {
                                console.error('Failed to mount tax Stripe element:', error);
                                this.taxPayment.cardError = 'Failed to initialize payment form. Please refresh and try again.';
                            }
                        } else {
                            console.error('Tax card element div not found');
                            this.taxPayment.cardError = 'Payment form container not found. Please refresh and try again.';
                        }
                    }, 100);
                }
            },
            async processTaxPayment() {
                this.taxPayment.processing = true;
                this.taxPayment.loadingMessage = 'Processing your tax payment...';
                this.taxPayment.cardError = null;

                try {
                    // Verify card element is ready
                    if (!this.taxPayment.cardElement) {
                        throw new Error('Card element not initialized. Please refresh and try again.');
                    }

                    // Confirm payment with Stripe using Payment Element
                    const result = await this.taxPayment.stripe.confirmPayment({
                        elements: this.taxPayment.elements,
                        confirmParams: {
                            return_url: window.location.href, // Required but won't be used since we handle success in JS
                        },
                        redirect: 'if_required', // Only redirect if absolutely necessary (e.g., 3D Secure)
                    });

                    if (result.error) {
                        this.taxPayment.cardError = result.error.message;
                        this.taxPayment.processing = false;
                    } else {
                        // Payment succeeded
                        this.taxPayment.success = true;
                        this.taxPayment.processing = false;

                        // Record the payment in the backend
                        await axios.post(apiUrl + 'api/account/' + this.code + '/' + this.id + '/record-tax-payment', {
                            payment_intent_id: result.paymentIntent.id,
                            amount: this.taxInfo.unpaidTaxAmount
                        });

                        // Reload account to show updated tax info
                        setTimeout(() => {
                            this.loadAccount();
                            this.loadTaxInfo();
                        }, 500);
                    }
                } catch (error) {
                    this.taxPayment.cardError = 'An error occurred processing your tax payment. Please try again.';
                    this.taxPayment.processing = false;
                    console.error('Tax payment error:', error);
                }
            },
            resetTaxPayment() {
                // Unmount the card element if it exists
                if (this.taxPayment.cardElement) {
                    try {
                        this.taxPayment.cardElement.unmount();
                    } catch (e) {
                        // Element might already be unmounted
                    }
                }

                // Keep the stripe instance but reset everything else
                const stripeInstance = this.taxPayment.stripe;

                this.taxPayment = {
                    step: 1,
                    agree: false,
                    processing: false,
                    success: false,
                    cardError: null,
                    stripe: stripeInstance,
                    elements: null,
                    cardElement: null,
                    paymentIntentClientSecret: null,
                    loadingMessage: 'Processing...'
                };

                // Initialize Stripe if needed
                if (!this.taxPayment.stripe) {
                    this.initializeTaxStripe();
                }

                // Reset tax plan state
                this.taxPlanMode = 'pay';
                this.taxPlanStatus = null;
                this.selectedTerm = null;
                this.taxPlanConfirming = false;
                this.taxPlanCreating = false;
                this.taxPlanCreated = null;
                this.taxPlanError = null;
            }
        },
        watch: {
            'id': function() {
                this.account.id = null;
                this.loadAccount();
                this.loadTaxInfo();
            }
        },
        mounted: function() {
            this.loadAccount();
            this.loadTaxInfo();
        }
    };
</script>

<style lang="scss" scoped>
    @import '~@/assets/scss/_variables.scss';

    .account {
        border: none;
    }

    .alerts-container {
        background: #f7f7f7;
        padding: 0 1.25rem 1.5rem 1.25rem;

        h6 {
            color: inherit;
        }
    }

    .tax-details-table {
        td {
            padding: 0.4rem 0.75rem;
            border-top: 1px solid #dee2e6;

            &:first-child {
                width: 250px;
            }
        }

        tbody tr:first-child td {
            border-top: none;
        }
    }

    h4 {
        font-size: 1.35rem;
        color: $gray-900;
    }

    h6 {
        font-size: 1rem;
        color: $gray-700;
    }

    .meta {
        font-size: 0.9rem;
        color: $gray-800;
    }

    .account-total {
        font-size: 1.1rem;
        font-weight: bold;

        @media only screen and (max-width: 600px) {
            text-align: left !important;
        }
    }

    .tabs {
        //border-top: 1px solid $gray-300;
        font-size: 0.9rem;
    }

    .details-table {
        width: 100%;

        th {
            text-transform: uppercase;
            padding: 0.5rem 1rem 0.5rem 1.2rem;
            border-top: 1px solid $gray-300;
            font-size: 0.8rem;
            min-width: 12rem;
            vertical-align: top;

            @media only screen and (max-width: 600px) {
                display: inline-block;
                width: 100%;
                margin-top: 20px;

            }
        }

        td {
            padding: 0.5rem 1rem 0.5rem 1.2rem;
            border-top: 1px solid $gray-300;
            width: 100%;
            vertical-align: top;

            @media only screen and (max-width: 600px) {
                padding: 0 1rem 0 1.2rem;

                display: inline-block;
                border-top: none;
            }
        }

        .no-border {
            border: 0;
        }
    }

    .payment-item {
        border-bottom: 1px solid $gray-300;

        &:last-child {
            border-bottom: 0;
        }
    }

    .fa-pencil {
        cursor: pointer;
        color: $gray-600;

        &:hover {
            color: $gray-800;
        }
    }

    .fa-exclamation-triangle {
        color: orange;
    }
</style>

<style lang="scss">
    @import '~@/assets/scss/_variables.scss';

    .card-body {
        background: #f7f7f7;
        border-top-left-radius: 0.25rem;
        border-top-right-radius: 0.25rem;
    }

    .card-header {
        background: #f7f7f7;
    }

    .nav-link {
        &.active {
            color: $gray-900;
            font-weight: bold;
        }

        color: $gray-700;
    }

    .table {
        margin-top: 0.5rem;

        th {
            font-size: 0.7rem;
            border-top: 0;
            color: $gray-600 !important;
            padding-bottom: 5px;

            &:first-child {
                padding-left: 1.2rem;
            }
        }

        td:first-child {
            padding-left: 1.2rem;
        }
    }

    .alert {
        font-size: 0.9rem;
    }

    .popover-body {
        font-size: 12px;
    }


    @media only screen and (max-width: 600px) {
        .nav-tabs .nav-item a {

            i {

                &.fa-exclamation-triangle {
                    display: inline-block;
                }
            }

            margin: 0 !important;
            padding: 0.2rem 0.3rem 0.2rem 0.4rem !important;
        }

        .transaction-table {
            width: 100%;
        }
    }
</style>
